import { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    Typography,
    Box,
    Chip,
    IconButton,
    Tooltip,
    Alert,
    CircularProgress
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ImageIcon from '@mui/icons-material/Image';
import QuizIcon from '@mui/icons-material/Quiz';
import { Order } from '../../../app/models/order';
import agent from '../../../app/api/agent';
import { toast } from 'react-toastify';
import { useAppDispatch } from '../../../app/store/configureStore';
import { fetchAdminOrderAsync } from './adminOrdersSlice';

interface Props {
    order: Order;
    open: boolean;
    onClose: () => void;
}

interface TestMaterialsComparison {
    order: {
        publicId: number;
        serviceName: string;
        isGraphical: boolean;
        packageTestPath: string | null;
        packageTestKeyPath: string | null;
        questionsJson: string | null;
        questionsCount: number;
    };
    service: {
        id: number;
        name: string;
        isGraphical: boolean;
        packageTestPath: string | null;
        packageTestKeyPath: string | null;
        questionsJson: string | null;
        questionsCount: number;
    };
}

export default function CopyTestMaterialsDialog({ order, open, onClose }: Props) {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [comparison, setComparison] = useState<TestMaterialsComparison | null>(null);
    const [copyPackageTestPath, setCopyPackageTestPath] = useState(false);
    const [copyPackageTestKeyPath, setCopyPackageTestKeyPath] = useState(false);
    const [copyQuestionsJson, setCopyQuestionsJson] = useState(false);
    const dispatch = useAppDispatch();

    const loadComparison = useCallback(async () => {
        setLoading(true);
        try {
            const data = await agent.Admin.Orders.getTestMaterialsComparison(order.publicId);
            setComparison(data);
            // Reset checkboxów
            setCopyPackageTestPath(false);
            setCopyPackageTestKeyPath(false);
            setCopyQuestionsJson(false);
        } catch (error) {
            console.error('Error loading comparison:', error);
            toast.error('Nie udało się załadować danych do porównania');
        } finally {
            setLoading(false);
        }
    }, [order.publicId]);

    useEffect(() => {
        if (open && order) {
            loadComparison();
        }
    }, [open, order, loadComparison]);

    const handleCopy = async () => {
        if (!copyPackageTestPath && !copyPackageTestKeyPath && !copyQuestionsJson) {
            toast.warning('Wybierz przynajmniej jeden materiał do skopiowania');
            return;
        }

        setSaving(true);
        try {
            await agent.Admin.Orders.copySelectedTestMaterials({
                orderId: order.publicId,
                copyPackageTestPath,
                copyPackageTestKeyPath,
                copyQuestionsJson
            });
            toast.success('Materiały zostały pomyślnie skopiowane do zamówienia');
            dispatch(fetchAdminOrderAsync(order.publicId));
            onClose();
        } catch (error: any) {
            console.error('Error copying materials:', error);
            const errorMessage = error?.data?.title || 'Nie udało się skopiować materiałów';
            toast.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const openPreview = (url: string | null | undefined) => {
        if (url) {
            window.open(url, '_blank');
        }
    };

    const handleGenerateTestPdf = async () => {
        try {
            await agent.Client.Orders.generateTestPdf(order.publicId);
            toast.success('Test PDF został wygenerowany i pobrany');
        } catch (error) {
            toast.error('Nie udało się wygenerować testu PDF');
        }
    };

    const isValueEmpty = (value: string | null | undefined): boolean => {
        return !value || value === '' || value === '[]' || value === '{}';
    };

    const renderValueStatus = (value: string | null | undefined, isUrl: boolean = false, questionsCount?: number) => {
        if (isValueEmpty(value)) {
            return (
                <Box display="flex" alignItems="center" gap={1}>
                    <CancelIcon color="error" fontSize="small" />
                    <Typography variant="body2" color="text.secondary">Brak</Typography>
                </Box>
            );
        }

        if (isUrl) {
            return (
                <Box display="flex" alignItems="center" gap={1}>
                    <CheckCircleIcon color="success" fontSize="small" />
                    <Typography variant="body2" color="success.main">Dostępny</Typography>
                    <Tooltip title="Otwórz w nowej karcie">
                        <IconButton size="small" onClick={() => openPreview(value)}>
                            <OpenInNewIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            );
        }

        // QuestionsJson
        return (
            <Box display="flex" alignItems="center" gap={1}>
                <CheckCircleIcon color="success" fontSize="small" />
                <Typography variant="body2" color="success.main">
                    {questionsCount} pytań
                </Typography>
            </Box>
        );
    };

    const canCopyPackageTestPath = comparison && !isValueEmpty(comparison.service.packageTestPath);
    const canCopyPackageTestKeyPath = comparison && !isValueEmpty(comparison.service.packageTestKeyPath);
    const canCopyQuestionsJson = comparison && !isValueEmpty(comparison.service.questionsJson);

    const orderHasPackageTestPath = comparison && !isValueEmpty(comparison.order.packageTestPath);
    const orderHasPackageTestKeyPath = comparison && !isValueEmpty(comparison.order.packageTestKeyPath);
    const orderHasQuestionsJson = comparison && !isValueEmpty(comparison.order.questionsJson);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <ContentCopyIcon />
                    <Typography variant="h6">Kopiuj materiały testowe z usługi</Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                        <CircularProgress />
                    </Box>
                ) : comparison ? (
                    <>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Wybierz materiały, które chcesz skopiować z usługi <strong>"{comparison.service.name}"</strong> do 
                            zamówienia <strong>#{comparison.order.publicId}</strong>.
                            {(orderHasPackageTestPath || orderHasPackageTestKeyPath || orderHasQuestionsJson) && (
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    <strong>Uwaga:</strong> Istniejące dane w zamówieniu zostaną nadpisane.
                                </Typography>
                            )}
                        </Alert>

                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: 'action.hover' }}>
                                        <TableCell width="30%"><strong>Materiał</strong></TableCell>
                                        <TableCell width="25%"><strong>Zamówienie #{comparison.order.publicId}</strong></TableCell>
                                        <TableCell width="25%"><strong>Usługa: {comparison.service.name}</strong></TableCell>
                                        <TableCell width="20%" align="center"><strong>Kopiuj</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {/* Sekcja dla testów graficznych */}
                                    <TableRow>
                                        <TableCell colSpan={4} sx={{ backgroundColor: 'primary.light', color: 'primary.contrastText' }}>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <ImageIcon />
                                                <Typography variant="subtitle2">
                                                    Testy graficzne
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>

                                    {/* Arkusz egzaminacyjny */}
                                    <TableRow hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="medium">
                                                Arkusz egzaminacyjny
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {renderValueStatus(comparison.order.packageTestPath, true)}
                                        </TableCell>
                                        <TableCell>
                                            {renderValueStatus(comparison.service.packageTestPath, true)}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title={!canCopyPackageTestPath ? "Usługa nie posiada arkusza" : orderHasPackageTestPath ? "Nadpisze istniejący arkusz" : "Skopiuj arkusz"}>
                                                <span>
                                                    <Checkbox
                                                        checked={copyPackageTestPath}
                                                        onChange={(e) => setCopyPackageTestPath(e.target.checked)}
                                                        disabled={!canCopyPackageTestPath}
                                                        color={orderHasPackageTestPath ? "warning" : "primary"}
                                                    />
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>

                                    {/* Klucz odpowiedzi */}
                                    <TableRow hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="medium">
                                                Klucz odpowiedzi
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {renderValueStatus(comparison.order.packageTestKeyPath, true)}
                                        </TableCell>
                                        <TableCell>
                                            {renderValueStatus(comparison.service.packageTestKeyPath, true)}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title={!canCopyPackageTestKeyPath ? "Usługa nie posiada klucza" : orderHasPackageTestKeyPath ? "Nadpisze istniejący klucz" : "Skopiuj klucz"}>
                                                <span>
                                                    <Checkbox
                                                        checked={copyPackageTestKeyPath}
                                                        onChange={(e) => setCopyPackageTestKeyPath(e.target.checked)}
                                                        disabled={!canCopyPackageTestKeyPath}
                                                        color={orderHasPackageTestKeyPath ? "warning" : "primary"}
                                                    />
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>

                                    {/* Sekcja dla testów tekstowych */}
                                    <TableRow>
                                        <TableCell colSpan={4} sx={{ backgroundColor: 'secondary.light', color: 'secondary.contrastText' }}>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <QuizIcon />
                                                <Typography variant="subtitle2">
                                                    Testy tekstowe
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>

                                    {/* Pytania JSON */}
                                    <TableRow hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="medium">
                                                Pytania egzaminacyjne
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                {renderValueStatus(
                                                    comparison.order.questionsJson, 
                                                    false, 
                                                    comparison.order.questionsCount
                                                )}
                                                {!isValueEmpty(comparison.order.questionsJson) && (
                                                    <Tooltip title="Pobierz test PDF">
                                                        <IconButton size="small" onClick={handleGenerateTestPdf}>
                                                            <OpenInNewIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {renderValueStatus(
                                                comparison.service.questionsJson, 
                                                false, 
                                                comparison.service.questionsCount
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title={!canCopyQuestionsJson ? "Usługa nie posiada pytań" : orderHasQuestionsJson ? "Nadpisze istniejące pytania" : "Skopiuj pytania"}>
                                                <span>
                                                    <Checkbox
                                                        checked={copyQuestionsJson}
                                                        onChange={(e) => setCopyQuestionsJson(e.target.checked)}
                                                        disabled={!canCopyQuestionsJson}
                                                        color={orderHasQuestionsJson ? "warning" : "primary"}
                                                    />
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Podsumowanie typu testu */}
                        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Chip 
                                label={`Typ zamówienia: ${comparison.order.isGraphical ? 'Graficzny' : 'Tekstowy'}`}
                                color={comparison.order.isGraphical ? 'primary' : 'secondary'}
                                variant="outlined"
                            />
                            <Chip 
                                label={`Typ usługi: ${comparison.service.isGraphical ? 'Graficzny' : 'Tekstowy'}`}
                                color={comparison.service.isGraphical ? 'primary' : 'secondary'}
                                variant="outlined"
                            />
                            {comparison.order.isGraphical !== comparison.service.isGraphical && (
                                <Chip 
                                    label="Uwaga: Różne typy testów!"
                                    color="warning"
                                />
                            )}
                        </Box>

                        {/* Ostrzeżenie o nadpisaniu */}
                        {((copyPackageTestPath && orderHasPackageTestPath) || 
                          (copyPackageTestKeyPath && orderHasPackageTestKeyPath) || 
                          (copyQuestionsJson && orderHasQuestionsJson)) && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                Wybrane materiały nadpiszą istniejące dane w zamówieniu!
                            </Alert>
                        )}
                    </>
                ) : (
                    <Alert severity="error">Nie udało się załadować danych</Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">
                    Anuluj
                </Button>
                <LoadingButton
                    onClick={handleCopy}
                    loading={saving}
                    variant="contained"
                    disabled={!copyPackageTestPath && !copyPackageTestKeyPath && !copyQuestionsJson}
                    startIcon={<ContentCopyIcon />}
                >
                    Kopiuj wybrane
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
